import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { CACHE_TIME } from '@/config/queryDefaults';

export interface ReadModelField {
  name: string;
  type: string;
  isDimension: boolean;
  isMeasure: boolean;
  aggregation?: string;
  description?: string;
  unit?: {
    type: string;
    label: string;
  };
}

export interface ReadModelMetadata {
  name: string;
  displayName: string;
  module: string;
  resolution: string;
  fields: ReadModelField[];
}

/** Raw API response shape (measures + dimensions, not fields) */
interface ReadModelMetadataApiResponse {
  name: string;
  displayName: string;
  module: string;
  resolution: string;
  measures: Array<{
    key: string;
    propertyName: string;
    aggregation: string;
    unitType: string;
    unitField?: string;
    unitSymbol?: string;
    unitLabel?: string;
  }>;
  dimensions: Array<{
    key: string;
    propertyName: string;
    dataType: string;
  }>;
}

/** Transform API response into the ReadModelMetadata shape used by the frontend */
function transformMetadataResponse(raw: ReadModelMetadataApiResponse): ReadModelMetadata {
  const fields: ReadModelField[] = [
    ...(raw.measures ?? []).map((m) => ({
      name: m.key,
      type: m.unitType ?? 'number',
      isDimension: false,
      isMeasure: true,
      aggregation: m.aggregation,
      description: m.propertyName,
      unit: m.unitLabel ? { type: m.unitType, label: m.unitLabel } : undefined,
    })),
    ...(raw.dimensions ?? []).map((d) => ({
      name: d.key,
      type: d.dataType,
      isDimension: true,
      isMeasure: false,
      description: d.propertyName,
    })),
  ];

  return {
    name: raw.name,
    displayName: raw.displayName,
    module: raw.module,
    resolution: raw.resolution,
    fields,
  };
}

export function useReadModelMetadata(readModelName: string) {
  return useQuery({
    queryKey: ['readModelMetadata', readModelName],
    queryFn: async () => {
      const { data } = await apiClient.get<ReadModelMetadataApiResponse>(
        `/api/${readModelName.includes('Sales') ? 'sales' : 'inventory'}/read-models/${toKebabCase(readModelName)}/metadata`,
      );
      return transformMetadataResponse(data);
    },
    enabled: !!readModelName,
    staleTime: CACHE_TIME.METADATA,
  });
}

export function useAllReadModelMetadata() {
  return useQuery({
    queryKey: ['readModelMetadata', 'all'],
    queryFn: async () => {
      // Fetch metadata from known read model endpoints
      const endpoints = [
        { name: 'SalesDaily', url: '/api/sales/read-models/sales-daily/metadata' },
        { name: 'InventorySnapshot', url: '/api/inventory/read-models/inventory-snapshot/metadata' },
      ];

      const results = await Promise.allSettled(
        endpoints.map(async (ep) => {
          const { data } = await apiClient.get<ReadModelMetadataApiResponse>(ep.url);
          return transformMetadataResponse(data);
        }),
      );

      return results
        .filter((r): r is PromiseFulfilledResult<ReadModelMetadata> => r.status === 'fulfilled')
        .map((r) => r.value);
    },
    staleTime: CACHE_TIME.METADATA,
  });
}

function toKebabCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}
